/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/useDirectDialForm.ts
 🗓️ Created: 2025-01-15
 🧩 Description:
    Custom hook for managing Direct Dial form state, validation, and submission.
────────────────────────────────────────────────────────────────*/

import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../../../hooks/useI18n';
import { useNotifications } from '../../../contexts/NotificationContext';
import { createDirectDial, updateDirectDial } from '../api/directDialApi';
import { createDefaultFormData } from '../utils/formDefaults';
import { generateContextFromName } from '../utils/contextHelpers';
import type { DirectDialFormState, ValidationErrors } from '../types';

interface UseDirectDialFormOptions {
  formData: DirectDialFormState;
  setFormData: React.Dispatch<React.SetStateAction<DirectDialFormState>>;
  originalFormData: DirectDialFormState;
  isEditMode: boolean;
  directDialId?: string;
}

export function useDirectDialForm({
  formData,
  setFormData,
  originalFormData,
  isEditMode,
  directDialId,
}: UseDirectDialFormOptions) {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { showSuccess, showError, showInfo } = useNotifications();
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [isSaving, setIsSaving] = useState(false);

  // Check if there are unsaved changes
  const hasChanges = useMemo(() => {
    return JSON.stringify(formData) !== JSON.stringify(originalFormData);
  }, [formData, originalFormData]);

  // Validation
  const validateForm = useCallback(() => {
    const newErrors: ValidationErrors = {};

    if (!formData.code.trim()) {
      newErrors.code = t('directDial.validation.codeRequired');
    } else if (!formData.code.match(/^\*?\d+$/)) {
      newErrors.code = t('directDial.validation.codeFormat');
    }

    if (!formData.name.trim()) {
      newErrors.name = t('directDial.validation.nameRequired');
    }

    if (!formData.destinationValue.trim()) {
      newErrors.destinationValue = t('directDial.validation.destinationValueRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, t]);

  // Handle save - Always-Active Save Standard: always attempt to save
  const handleSave = useCallback(async () => {
    if (!validateForm()) {
      showError(t('directDial.messages.validationError'));
      return undefined;
    }

    setIsSaving(true);
    try {
      // Generate context from name and use name as description
      const generatedContext = generateContextFromName(formData.name);
      const dataToSave = {
        ...formData,
        context: generatedContext,
        description: formData.name
      };

      let savedData;
      if (isEditMode && directDialId) {
        savedData = await updateDirectDial(directDialId, dataToSave);
        showSuccess(t('directDial.messages.updatedSuccess'));
      } else {
        savedData = await createDirectDial(dataToSave);
        showSuccess(t('directDial.messages.createdSuccess'));
        // Note: Navigation after create is now handled by the dropdown actions
        // in DirectDialFormView, so we don't navigate here automatically
      }

      // Update form data with saved values
      const newFormData = {
        code: savedData.code,
        name: savedData.name,
        description: savedData.description || savedData.name,
        context: savedData.context,
        destinationModule: savedData.destinationModule,
        destinationValue: savedData.destinationValue,
        enabled: savedData.enabled
      };
      setFormData(newFormData);

      return savedData;
    } catch (error) {
      console.error('Error saving direct dial:', error);
      const msg = error instanceof Error ? error.message : t('directDial.messages.saveError');
      showError(msg);
      throw error;
    } finally {
      setIsSaving(false);
    }
  }, [
    formData,
    validateForm,
    isEditMode,
    directDialId,
    hasChanges,
    showSuccess,
    showError,
    showInfo,
    t,
    setFormData,
  ]);

  // Handle cancel
  const handleCancel = useCallback(() => {
    if (isEditMode) {
      setFormData({ ...originalFormData });
    } else {
      setFormData(createDefaultFormData());
    }
    setErrors({});
  }, [isEditMode, originalFormData, setFormData]);

  return {
    errors,
    setErrors,
    isSaving,
    hasChanges,
    validateForm,
    handleSave,
    handleCancel,
  };
}

